import React, { useState, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { MapPin, Mail, Phone, Send, Loader2, Clock, Users, Truck, Upload, Briefcase } from 'lucide-react';
import { FlagImg } from '../components/FlagImg';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const countries = [
  { code: 'ES', nameKey: 'spain' },
  { code: 'PT', nameKey: 'portugal' },
  { code: 'FR', nameKey: 'france' },
  { code: 'BE', nameKey: 'belgium' },
  { code: 'NL', nameKey: 'netherlands' },
  { code: 'DE', nameKey: 'germany' },
];

const locations = [
  {
    city: 'Seville',
    nameKey: 'sevilleLocationName',
    countryCode: 'ES',
    address: 'Carretera de la Esclusa, s/n, Zona Franca Puerto de Sevilla, 41011 Sevilla',
    phone: '+34 954286290',
    email: 'seville@trhsteel.com',
    hours: '08:30 – 17:00',
  },
  {
    city: 'Liège',
    nameKey: 'liegeLocationName',
    countryCode: 'BE',
    address: "Rue de l'Ile Monsin, 107, 4020 Liège, Bélgica",
    phone: '+32 43460119',
    email: 'liege@trhsteel.com',
    hours: '06:00 – 19:00',
  },
];

export const Contact = () => {
  const { t, language } = useLanguage();

  // Contact form state
  const [formData, setFormData] = useState({
    name: '', email: '', company: '', country: '', phone: '', subject: '', message: '',
  });
  const [loading, setLoading] = useState(false);

  // Career form state
  const [careerData, setCareerData] = useState({
    name: '', email: '', phone: '', message: '',
  });
  const [careerFile, setCareerFile] = useState(null);
  const [careerLoading, setCareerLoading] = useState(false);
  const careerFileRef = useRef(null);

  // Transport partner form state
  const [transportData, setTransportData] = useState({
    company_name: '', contact_person: '', email: '', phone: '', coverage_area: '', fleet_description: '',
  });
  const [transportLoading, setTransportLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...formData, language });
      toast.success(t('messageSent'));
      setFormData({ name: '', email: '', company: '', country: '', phone: '', subject: '', message: '' });
    } catch {
      toast.error(t('messageError'));
    } finally {
      setLoading(false);
    }
  };

  const handleCareerSubmit = async (e) => {
    e.preventDefault();
    setCareerLoading(true);
    try {
      let cv_data = null;
      let cv_filename = null;
      if (careerFile) {
        cv_filename = careerFile.name;
        const reader = new FileReader();
        cv_data = await new Promise((resolve) => {
          reader.onload = () => resolve(reader.result.split(',')[1]);
          reader.readAsDataURL(careerFile);
        });
      }
      await axios.post(`${API}/careers`, { ...careerData, cv_filename, cv_data, language });
      toast.success(t('applicationSent'));
      setCareerData({ name: '', email: '', phone: '', message: '' });
      setCareerFile(null);
      if (careerFileRef.current) careerFileRef.current.value = '';
    } catch {
      toast.error(t('applicationError'));
    } finally {
      setCareerLoading(false);
    }
  };

  const handleTransportSubmit = async (e) => {
    e.preventDefault();
    setTransportLoading(true);
    try {
      await axios.post(`${API}/transport-partners`, { ...transportData, language });
      toast.success(t('partnerSent'));
      setTransportData({ company_name: '', contact_person: '', email: '', phone: '', coverage_area: '', fleet_description: '' });
    } catch {
      toast.error(t('partnerError'));
    } finally {
      setTransportLoading(false);
    }
  };

  return (
    <div className="bg-slate-50 min-h-screen" data-testid="contact-page">
      {/* Hero */}
      <section className="py-20 md:py-24 bg-slate-900 text-white" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tight mb-4">
            {t('contactTitle')}
          </h1>
          <p className="text-slate-300 text-lg max-w-2xl">
            {t('contactSubtitle')}
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-12 md:py-20" data-testid="contact-content">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Contact Form */}
            <div>
              <h2 className="font-heading text-2xl font-bold text-slate-900 uppercase tracking-tight mb-6">
                {t('sendMessage')}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-sm font-medium text-slate-700">{t('yourName')} *</Label>
                    <Input id="name" name="name" value={formData.name} onChange={handleChange} required className="rounded-none border-slate-300" data-testid="input-name" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-medium text-slate-700">{t('yourEmail')} *</Label>
                    <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required className="rounded-none border-slate-300" data-testid="input-email" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="company" className="text-sm font-medium text-slate-700">{t('company')}</Label>
                    <Input id="company" name="company" value={formData.company} onChange={handleChange} className="rounded-none border-slate-300" data-testid="input-company" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="country" className="text-sm font-medium text-slate-700">{t('country')} *</Label>
                    <Select value={formData.country} onValueChange={(v) => setFormData(p => ({ ...p, country: v }))}>
                      <SelectTrigger className="rounded-none border-slate-300" data-testid="select-country">
                        <SelectValue placeholder={t('selectCountry')} />
                      </SelectTrigger>
                      <SelectContent>
                        {countries.map((c) => (
                          <SelectItem key={c.code} value={c.code}>
                            <span className="flex items-center gap-2"><FlagImg code={c.code} size={18} /> {t(c.nameKey)}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-sm font-medium text-slate-700">{t('phone')}</Label>
                    <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} className="rounded-none border-slate-300" data-testid="input-phone" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="subject" className="text-sm font-medium text-slate-700">{t('subject')} *</Label>
                    <Input id="subject" name="subject" value={formData.subject} onChange={handleChange} required className="rounded-none border-slate-300" data-testid="input-subject" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="message" className="text-sm font-medium text-slate-700">{t('message')} *</Label>
                  <Textarea id="message" name="message" value={formData.message} onChange={handleChange} required rows={6} className="rounded-none border-slate-300 resize-none" data-testid="input-message" />
                </div>
                <Button type="submit" disabled={loading} className="w-full sm:w-auto bg-slate-900 hover:bg-slate-800 text-white rounded-none uppercase tracking-wider font-bold px-8 py-4" data-testid="submit-button">
                  {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('sending')}</> : <><Send className="h-4 w-4 mr-2" />{t('sendMessage')}</>}
                </Button>
              </form>
            </div>

            {/* Locations */}
            <div>
              <h2 className="font-heading text-2xl font-bold text-slate-900 uppercase tracking-tight mb-6">
                {t('ourLocations')}
              </h2>
              <div className="space-y-6">
                {locations.map((location) => (
                  <div key={location.city} className="bg-white border border-slate-200 p-6" data-testid={`location-${location.city.toLowerCase()}`}>
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-slate-900 flex items-center justify-center flex-shrink-0">
                        <MapPin className="h-6 w-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-heading text-xl font-bold text-slate-900 uppercase tracking-tight mb-1">
                          {t(location.nameKey)} <FlagImg code={location.countryCode} size={22} className="ml-1" />
                        </h3>
                        <p className="text-slate-600 text-sm mb-4">{location.address}</p>
                        <div className="space-y-2">
                          <a href={`tel:${location.phone}`} className="flex items-center space-x-2 text-sm text-slate-600 hover:text-slate-900 transition-colors">
                            <Phone className="h-4 w-4" /><span>{location.phone}</span>
                          </a>
                          <a href={`mailto:${location.email}`} className="flex items-center space-x-2 text-sm text-slate-600 hover:text-slate-900 transition-colors">
                            <Mail className="h-4 w-4" /><span>{location.email}</span>
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                <div className="bg-slate-900 text-white p-6">
                  <h3 className="font-heading text-xl font-bold uppercase tracking-tight mb-4">{t('generalInquiries')}</h3>
                  <a href="mailto:info@trhsteel.com" className="flex items-center space-x-2 text-slate-300 hover:text-white transition-colors">
                    <Mail className="h-5 w-5" /><span className="text-lg">info@trhsteel.com</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Loading Hours */}
      <section className="py-12 md:py-16 bg-white border-y border-slate-200" data-testid="loading-hours">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-3 mb-8">
            <div className="w-10 h-10 bg-slate-900 flex items-center justify-center">
              <Clock className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="font-heading text-2xl font-bold text-slate-900 uppercase tracking-tight">
                {t('loadingHours')}
              </h2>
              <p className="text-slate-500 text-sm">{t('loadingHoursDesc')}</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-6 max-w-2xl">
            {locations.map((loc) => (
              <div key={loc.city} className="flex items-center space-x-4 p-5 bg-slate-50 border border-slate-200" data-testid={`hours-${loc.city.toLowerCase()}`}>
                <FlagImg code={loc.countryCode} size={28} />
                <div>
                  <div className="font-heading text-base font-bold text-slate-900 uppercase tracking-tight">TRH {loc.city}</div>
                  <div className="text-lg font-mono font-bold text-green-700 mt-1">{loc.hours}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Careers Section */}
      <section className="py-12 md:py-20 bg-slate-50" data-testid="careers-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
            <div className="lg:col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-slate-900 flex items-center justify-center">
                  <Briefcase className="h-5 w-5 text-white" />
                </div>
                <h2 className="font-heading text-2xl font-bold text-slate-900 uppercase tracking-tight">
                  {t('joinOurTeam')}
                </h2>
              </div>
              <p className="text-slate-600 leading-relaxed">
                {t('joinOurTeamDesc')}
              </p>
            </div>
            <div className="lg:col-span-3">
              <form onSubmit={handleCareerSubmit} className="bg-white border border-slate-200 p-6 md:p-8 space-y-5" data-testid="career-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">{t('yourName')} *</Label>
                    <Input value={careerData.name} onChange={(e) => setCareerData(p => ({ ...p, name: e.target.value }))} required className="rounded-none border-slate-300" data-testid="career-name" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-700">{t('yourEmail')} *</Label>
                    <Input type="email" value={careerData.email} onChange={(e) => setCareerData(p => ({ ...p, email: e.target.value }))} required className="rounded-none border-slate-300" data-testid="career-email" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">{t('phone')}</Label>
                  <Input type="tel" value={careerData.phone} onChange={(e) => setCareerData(p => ({ ...p, phone: e.target.value }))} className="rounded-none border-slate-300" data-testid="career-phone" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">{t('cvUpload')} *</Label>
                  <div className="flex items-center space-x-3">
                    <label className="flex items-center space-x-2 px-4 py-2.5 border border-slate-300 bg-white hover:bg-slate-50 cursor-pointer transition-colors text-sm text-slate-700">
                      <Upload className="h-4 w-4" />
                      <span>{careerFile ? careerFile.name : t('cvUpload')}</span>
                      <input
                        ref={careerFileRef}
                        type="file"
                        accept=".pdf,.doc,.docx"
                        className="hidden"
                        onChange={(e) => setCareerFile(e.target.files[0] || null)}
                        data-testid="career-cv-upload"
                      />
                    </label>
                  </div>
                  <p className="text-xs text-slate-400">{t('cvUploadHint')}</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">{t('message')}</Label>
                  <Textarea value={careerData.message} onChange={(e) => setCareerData(p => ({ ...p, message: e.target.value }))} rows={3} className="rounded-none border-slate-300 resize-none" placeholder="" data-testid="career-message" />
                </div>
                <Button type="submit" disabled={careerLoading} className="w-full sm:w-auto bg-slate-900 hover:bg-slate-800 text-white rounded-none uppercase tracking-wider font-bold px-8 py-4" data-testid="career-submit">
                  {careerLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('sending')}</> : <><Users className="h-4 w-4 mr-2" />{t('sendApplication')}</>}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Transport Partners Section */}
      <section className="py-12 md:py-20 bg-slate-900 text-white" data-testid="transport-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
            <div className="lg:col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-green-600 flex items-center justify-center">
                  <Truck className="h-5 w-5 text-white" />
                </div>
                <h2 className="font-heading text-2xl font-bold uppercase tracking-tight">
                  {t('transportPartners')}
                </h2>
              </div>
              <p className="text-slate-400 leading-relaxed">
                {t('transportPartnersDesc')}
              </p>
            </div>
            <div className="lg:col-span-3">
              <form onSubmit={handleTransportSubmit} className="bg-slate-800 border border-slate-700 p-6 md:p-8 space-y-5" data-testid="transport-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-300">{t('companyName')} *</Label>
                    <Input value={transportData.company_name} onChange={(e) => setTransportData(p => ({ ...p, company_name: e.target.value }))} required className="rounded-none border-slate-600 bg-slate-700 text-white placeholder:text-slate-400" data-testid="transport-company" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-300">{t('contactPerson')} *</Label>
                    <Input value={transportData.contact_person} onChange={(e) => setTransportData(p => ({ ...p, contact_person: e.target.value }))} required className="rounded-none border-slate-600 bg-slate-700 text-white placeholder:text-slate-400" data-testid="transport-contact" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-300">{t('yourEmail')} *</Label>
                    <Input type="email" value={transportData.email} onChange={(e) => setTransportData(p => ({ ...p, email: e.target.value }))} required className="rounded-none border-slate-600 bg-slate-700 text-white placeholder:text-slate-400" data-testid="transport-email" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-slate-300">{t('phone')} *</Label>
                    <Input type="tel" value={transportData.phone} onChange={(e) => setTransportData(p => ({ ...p, phone: e.target.value }))} required className="rounded-none border-slate-600 bg-slate-700 text-white placeholder:text-slate-400" data-testid="transport-phone" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-300">{t('coverageArea')} *</Label>
                  <Input value={transportData.coverage_area} onChange={(e) => setTransportData(p => ({ ...p, coverage_area: e.target.value }))} required placeholder={t('coverageAreaPlaceholder')} className="rounded-none border-slate-600 bg-slate-700 text-white placeholder:text-slate-400" data-testid="transport-coverage" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-300">{t('fleetDescription')} *</Label>
                  <Textarea value={transportData.fleet_description} onChange={(e) => setTransportData(p => ({ ...p, fleet_description: e.target.value }))} required rows={3} placeholder={t('fleetDescriptionPlaceholder')} className="rounded-none border-slate-600 bg-slate-700 text-white placeholder:text-slate-400 resize-none" data-testid="transport-fleet" />
                </div>
                <Button type="submit" disabled={transportLoading} className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white rounded-none uppercase tracking-wider font-bold px-8 py-4" data-testid="transport-submit">
                  {transportLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('sending')}</> : <><Truck className="h-4 w-4 mr-2" />{t('becomePartner')}</>}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
